// services/admin/admin-newsletterService.js
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const adminNewsletterApi = axios.create({
  baseURL: `${API_URL}/api/v1/admin`
});

/* ── Attach admin token ── */
adminNewsletterApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (!(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* ── Redirect to admin login on 401 ── */
adminNewsletterApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

const adminNewsletterService = {
  /* ── List with filters + pagination ── */
  async getSubscribers({ page = 1, limit = 20, search, status, sort = 'newest' } = {}) {
    const q = new URLSearchParams();
    q.append('page', page);
    q.append('limit', limit);
    if (search) q.append('search', search);
    if (status && status !== 'all') q.append('status', status);
    q.append('sort', sort);

    const res = await adminNewsletterApi.get(`/newsletter?${q.toString()}`);
    return res.data;
  },

  /* ── Download CSV ── */
  async exportCsv({ search, status } = {}) {
    const q = new URLSearchParams();
    if (search) q.append('search', search);
    if (status && status !== 'all') q.append('status', status);

    const res = await adminNewsletterApi.get(
      `/newsletter/export?${q.toString()}`,
      { responseType: 'blob' }
    );

    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `kapta-newsletter-${new Date().toISOString().slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return { success: true };
  },

  /* ── Toggle subscriber status ── */
  async updateStatus(id, status) {
    const res = await adminNewsletterApi.patch(
      `/newsletter/${id}/status`,
      { status }
    );
    return res.data;
  },

  /* ── UI helpers ── */
  formatDate(dateStr) {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  },

  getStatusBadgeConfig(status) {
    if (status === 'subscribed') {
      return {
        bgColor: 'bg-[#E7F4ED]',
        textColor: 'text-[#2F8F5B]',
        dotColor: 'bg-[#2F8F5B]'
      };
    }
    return {
      bgColor: 'bg-[#F4E7E7]',
      textColor: 'text-[#A82121]',
      dotColor: 'bg-[#A82121]'
    };
  }
};

export default adminNewsletterService;